<template>
    <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200&icon_names=edit_square,help,delete"
    />

    <div class="config-form-wrapper">
        <div class="sidebar-esquerda">
            <AbaMenuLateral :tabs="tabs">
                <template #after>
                    <Button @click="adicionarCampoPersonalizado" style="width: 100%" label="Campo Personalizado" icon="pi pi-plus" class="p-button-lg" />
                </template>
            </AbaMenuLateral>
        </div>

        <div class="formulario-central" :class="{ 'formulario-calculated-height': !isFullscreen, 'formulario-full-screen': isFullscreen }">
            <div class="formulario-editor">
                <div class="formulario-card" :style="formularioCardStyle">
                    <h3 :style="{ fontFamily: designFormularioAtivo.tipografia, fontSize: designFormularioAtivo.tamanhoFonte }">Formulário</h3>
                    <div>
                        <TextInput required label="CPF" />
                    </div>
                    <Dropzone
                        v-if="formularioAtivoStore?.componenteId"
                        :componenteId="formularioAtivoStore.componenteId"
                        :dropzone-class="['formulario-dropzone']"
                        :placeholder-class="['formulario-placeholder']"
                        :dropzoneStyle="{ padding: '0px', display: 'flex', flexDirection: 'column', gap: designFormularioAtivo.gap }"
                    >
                        <template #placeholder>
                            <span class="material-symbols-outlined" style="font-size: 36px"> dynamic_form </span>
                            <span>Arraste campos do formulário para essa área</span>
                        </template>
                    </Dropzone>
                    <div class="botoes-formulario" :style="estiloContainerBotoes">
                        <Button
                            v-if="designFormularioAtivo.botaoLimpar"
                            label="Limpar Campos"
                            class="p-button-lg"
                            @click="selecionarBotaoLimparCampos"
                            :style="estiloBotaoLimpar"
                        />
                        <Button @click="selecionarBotaoFormulario" :label="botaoFormulario.props.texto" class="p-button-lg" :style="estiloBotaoInscricao" />
                    </div>
                </div>
            </div>
        </div>

        <div class="sidebar-direita">
            <DesignFormulario :modelValue="designFormularioAtivo" @design-change="handleDesignChange" />
            <AbaMenuConfig v-if="idElementoSelecionado != null" />
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import AbaMenuConfig from '@/components/editorLandingPage/core/menuConfig/AbaMenuConfig.vue';
import { useStore } from 'vuex';
import AbaMenuLateral from '@/components/editorLandingPage/core/AbaMenuLateral.vue';
import { useFullscreen } from '@vueuse/core';
import { CAMPOS_DISPONIVEIS } from '@/constants/componentesCatalogo';
import { useEditorLandingPage } from '@/composables/useEditorLandingPage';
import Dropzone from '@/components/editorLandingPage/core/Dropzone.vue';
import TextInput from '@/components/editorLandingPage/componentes/form/TextInput.vue';
import DesignFormulario from '@/components/editorLandingPage/core/DesingFormulario.vue';

const tabs = [{ header: 'Campos do Formulário', list: CAMPOS_DISPONIVEIS }];

const { idElementoSelecionado, adicionarCampoPersonalizado, selecionarBotaoFormulario, botaoFormulario, selecionarBotaoLimparCampos } = useEditorLandingPage();
const { isFullscreen } = useFullscreen();
const store = useStore();

const corFocusBordaBotao = computed(() => `0 0 0 0.2rem ${botaoFormulario.value.props.cor}66`);
const isCorClara = (hex) => {
    if (!hex) return true;

    const cleanHex = hex.replace('#', '');

    const r = parseInt(cleanHex.substr(0, 2), 16);
    const g = parseInt(cleanHex.substr(2, 2), 16);
    const b = parseInt(cleanHex.substr(4, 2), 16);

    const luminancia = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    return luminancia > 0.5;
};
const corTexto = computed(() => {
    return isCorClara(botaoFormulario.value.props.cor) ? '#000000' : '#ffffff';
});

const corTextoBotaoLimpar = computed(() => {
    return isCorClara(designFormularioAtivo.value.botaoLimparCor) ? '#000000' : '#ffffff';
});

const getAlignmentStyle = (alinhamento) => {
    if (alinhamento === 'center') {
        return { left: '50%', transform: 'translateX(-50%)' };
    }
    if (alinhamento === 'end') {
        return { right: '0px' };
    }
    return { left: '0px' };
};

const estiloContainerBotoes = computed(() => {
    const temBotaoLimpar = designFormularioAtivo.value.botaoLimpar;
    const alinhamentoLimpar = designFormularioAtivo.value.botaoLimparAlinhamento;
    const alinhamentoPrincipal = botaoFormulario.value.props.alinhamento;

    // Cenário 1: Agrupado. O alinhamento principal controla o grupo.
    if (!temBotaoLimpar || alinhamentoLimpar === 'end') {
        return { justifyContent: alinhamentoPrincipal };
    }

    // Cenário 2: Desagrupado com alinhamentos iguais. Flexbox controla o alinhamento lado a lado.
    if (alinhamentoLimpar === alinhamentoPrincipal) {
        return { justifyContent: alinhamentoPrincipal };
    }

    // Cenário 3: Desagrupado com alinhamentos diferentes. Posicionamento absoluto.
    return { position: 'relative', minHeight: '50px' };
});

const estiloBotaoInscricao = computed(() => {
    const style = {
        backgroundColor: botaoFormulario.value.props.cor,
        borderColor: botaoFormulario.value.props.cor,
        color: corTexto.value,
    };

    const temBotaoLimpar = designFormularioAtivo.value.botaoLimpar;
    const alinhamentoLimpar = designFormularioAtivo.value.botaoLimparAlinhamento;
    const alinhamentoPrincipal = botaoFormulario.value.props.alinhamento;

    // Aplica posicionamento absoluto apenas no cenário 3.
    if (temBotaoLimpar && alinhamentoLimpar !== 'end' && alinhamentoLimpar !== alinhamentoPrincipal) {
        Object.assign(style, {
            position: 'absolute',
            ...getAlignmentStyle(alinhamentoPrincipal),
        });
    }

    return style;
});

const estiloBotaoLimpar = computed(() => {
    const style = {
        backgroundColor: designFormularioAtivo.value.botaoLimparCor,
        borderColor: designFormularioAtivo.value.botaoLimparCor,
        color: corTextoBotaoLimpar.value,
    };

    const temBotaoLimpar = designFormularioAtivo.value.botaoLimpar;
    const alinhamentoLimpar = designFormularioAtivo.value.botaoLimparAlinhamento;
    const alinhamentoPrincipal = botaoFormulario.value.props.alinhamento;

    // Aplica posicionamento absoluto apenas no cenário 3.
    if (temBotaoLimpar && alinhamentoLimpar !== 'end' && alinhamentoLimpar !== alinhamentoPrincipal) {
        Object.assign(style, {
            position: 'absolute',
            ...getAlignmentStyle(alinhamentoLimpar),
        });
    }

    return style;
});

const formularioAtivoStore = computed(() => store.getters['landingPage/formularioAtivo']);
const designFormularioAtivo = computed(() => store.getters['landingPage/designFormularioAtivo']);

const handleDesignChange = (novoDesign) => {
    store.dispatch('landingPage/atualizarDesignFormulario', {
        formularioId: formularioAtivoStore.value.componenteId,
        design: novoDesign,
    });
};

const formularioCardStyle = computed(() => ({
    backgroundColor: designFormularioAtivo.value.corFundo,
    backgroundImage: designFormularioAtivo.value.imagemFundo ? `url(${designFormularioAtivo.value.imagemFundo})` : 'none',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    fontFamily: designFormularioAtivo.value.tipografia,
    padding: designFormularioAtivo.value.padding,
    display: 'flex',
    flexDirection: 'column',
}));
</script>

<style lang="scss" scoped>
.botoes-formulario {
    display: flex;
    gap: 16px;
    margin-top: 16px;
}
.p-button:focus {
    box-shadow: v-bind(corFocusBordaBotao) !important;
}

.config-form-wrapper {
    display: flex;
    flex-grow: 1;
    overflow: hidden;
}

.sidebar-esquerda,
.sidebar-direita {
    position: relative;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.sidebar-conteudo {
    display: flex;
    flex-direction: column;
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    overflow-y: auto;
}

.formulario-central {
    flex: 3;
    display: flex;
    flex-direction: column;
    align-items: center;
    background: #fff;
    border-left: 1px solid #cccccc;
    border-right: 1px solid #cccccc;
    overflow-y: auto;
}

.formulario-calculated-height {
    /*
    Me perdoe por isso aqui, mas não tinha tempo de fazer de um jeito melhor - Renan
    100vh = altura da tela
    52px = altura do header do editor
    65px = altura do footer do editor
    16px = padding top da tela
    16px = padding bottom da tela
    70px = altura do header da página
    44px = altura do footer da página
    */
    height: calc(100vh - 52px - 65px - 16px - 16px - 70px - 44px);
}

.formulario-full-screen {
    /*
    Me perdoe por isso aqui, mas não tinha tempo de fazer de um jeito melhor - Renan
    100vh = altura da tela
    52px = altura do header do editor
    65px = altura do footer do editor
    */
    height: calc(100vh - 52px - 65px);
}

.formulario-editor {
    padding: 12px 48px;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    width: 100%;
}

.formulario-card {
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 8px;
    width: 100%;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    position: relative;

    h3 {
        margin-top: 0;
        margin-bottom: 24px;
        color: #333;
        font-size: 1.25em;
    }
}

.component-container {
    position: relative;
    margin-bottom: 16px;

    &.draggable-item {
        cursor: grab;

        &:active {
            cursor: grabbing;
        }
    }

    &.campo-obrigatorio {
        :deep(label) {
            &::after {
                content: '*';
                color: #dc2626;
                font-weight: bold;
            }
        }

        :deep(.field-label::after),
        :deep(.form-label::after),
        :deep(.input-label::after) {
            content: '*';
            color: #dc2626;
            font-weight: bold;
        }
    }
}

.actions {
    display: flex;
    justify-content: space-between;
    flex-grow: 1;
}

.title-actions,
.input-actions {
    display: flex;
    gap: 6px;
    z-index: 10;
    align-items: center;
}

.input-actions {
    gap: 4px;
}

.action-btn {
    border: none;
    background: transparent;
    cursor: pointer;
    padding: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 0.7;
    }

    .material-symbols-outlined {
        font-size: 20px;
        font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 20;
    }
}

.edit-btn .material-symbols-outlined {
    color: #0f97c7;
}

.help-btn .material-symbols-outlined {
    color: #1c1b1f;
}

.delete-btn .material-symbols-outlined {
    color: #a22818;
}

.drag-area {
    border: 2px dashed transparent;
    transition: border-color 0.2s;

    &:hover {
        border-color: #0f97c7 !important;
        border-style: dashed !important;
    }
}

.formulario-campos-container {
    background: transparent;
    border: none;
    border-radius: 0;
    padding: 0;
}

.flex {
    display: flex;

    &.flex-row {
        flex-direction: row;
    }

    &.flex-column {
        flex-direction: column;
    }

    &.flex-1 {
        flex: 1;
    }
}
</style>
